import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const ACCESS_TOKEN_KEY  = "flowbtp_access_token";
export const REFRESH_TOKEN_KEY = "flowbtp_refresh_token";

export const tokens = {
  get access()  { return localStorage.getItem(ACCESS_TOKEN_KEY);  },
  get refresh() { return localStorage.getItem(REFRESH_TOKEN_KEY); },
  set(access, refresh) {
    if (access)  localStorage.setItem(ACCESS_TOKEN_KEY,  access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  // Sans borne, une requête partie sur un réseau de chantier dégradé laisse
  // l'écran tourner indéfiniment au lieu de basculer sur un état d'erreur.
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = tokens.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshInFlight = null;

async function doRefresh() {
  const refreshToken = tokens.refresh;
  if (!refreshToken) throw new Error("no refresh token");
  const resp = await axios.post(`${API_BASE}/auth/refresh`, {
    refresh_token: refreshToken,
  });
  tokens.set(resp.data.access_token, null);
  return resp.data.access_token;
}

// Rafraîchit l'access token en dédupliquant les appels concurrents.
export async function refreshAccessToken() {
  if (!refreshInFlight) refreshInFlight = doRefresh();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// Throttle toast par endpoint (4s) — évite le spam sur burst de requêtes.
const _lastToastTs = new Map(); // key: `status:method:url` -> ts
function _shouldShowToast(key) {
  const now  = Date.now();
  const last = _lastToastTs.get(key) || 0;
  if (now - last < 4000) return false;
  _lastToastTs.set(key, now);
  return true;
}

// v1.0.2 — Throttle server-error toasts (5xx)
function _maybeToastServerError(error) {
  const status = error?.response?.status;
  if (typeof status !== "number" || status < 500) return;
  const cfg = error.config || {};
  const key = `${status}:${(cfg.method || "get").toUpperCase()}:${cfg.url || "?"}`;
  if (!_shouldShowToast(key)) return;
  const detail = error?.response?.data?.detail;
  const msg =
    (typeof detail === "string" && detail) ||
    "Erreur serveur — merci de réessayer dans un instant.";
  toast.error(msg, {
    description: `Statut ${status} sur ${cfg.url || "la requête"}`,
  });
}

/**
 * V2-T9 — Toast 403 centralisé : message humain + bouton Retour.
 * Pas de jargon technique, pas de stack trace.
 *
 * BUG-026 — il affichait « Vous n'avez pas les droits pour cette action » pour
 * TOUT 403, sans distinguer le geste de l'utilisateur des lectures que la page
 * lance toute seule. Un chef de chantier qui ouvrait le formulaire de tâche
 * voyait un bandeau rouge et un bouton « ← Retour » : le serveur lui AUTORISE
 * pourtant la création (POST .../tasks répond 200), et le formulaire s'ouvrait
 * derrière le bandeau. Ce qui était refusé, c'étaient deux lectures d'appoint
 * (`GET /users`, `GET /settings/metier-catalog`). Le message les attribuait à
 * l'action en cours et proposait de quitter la page — un chef de chantier
 * abandonnait une tâche qu'il avait le droit de créer.
 *
 * Une lecture refusée ne se commente donc plus : c'est à l'écran concerné de
 * montrer une liste vide explicite. Seules les écritures — celles que
 * l'utilisateur a réellement déclenchées — méritent le bandeau.
 */
const _METHODES_SANS_BANDEAU_403 = new Set(["GET", "HEAD", "OPTIONS"]);

function _maybeToast403(error) {
  const status = error?.response?.status;
  if (status !== 403) return;
  const cfg = error.config || {};
  if (_METHODES_SANS_BANDEAU_403.has((cfg.method || "get").toUpperCase())) return;
  const key = `403:${(cfg.method || "get").toUpperCase()}:${cfg.url || "?"}`;
  if (!_shouldShowToast(key)) return;

  const detail = error?.response?.data?.detail;
  // Message principal : toujours humain, jamais de jargon.
  const title = "Vous n'avez pas les droits pour cette action.";
  // Description : utilise le détail backend s'il est court et lisible,
  // sinon message générique.
  const description =
    (typeof detail === "string" && detail.length < 180 && detail !== "Permissions insuffisantes")
      ? detail
      : "Cette action est réservée à un rôle avec davantage de permissions.";

  toast.error(title, {
    description,
    duration: 8000,
    action: {
      label: "← Retour",
      onClick: () => {
        if (typeof window !== "undefined") window.history.back();
      },
    },
  });
}

/**
 * V2-T9 — Toast 401 non-retriable (session expirée / déconnecté).
 * Discret : info, pas error, car la redirection /login va suivre.
 */
function _maybeToast401Expired(error) {
  const status = error?.response?.status;
  if (status !== 401) return;
  const cfg = error.config || {};
  const key = `401:${(cfg.method || "get").toUpperCase()}:${cfg.url || "?"}`;
  if (!_shouldShowToast(key)) return;
  toast.info("Session expirée. Reconnexion en cours…", { duration: 3000 });
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original     = error.config || {};
    const status       = error.response?.status;
    const isAuthEndpoint =
      original.url?.includes("/auth/login") ||
      original.url?.includes("/auth/register") ||
      original.url?.includes("/auth/refresh");

    // ── 401 : tenter le refresh token ────────────────────────────────────────
    if (status === 401 && !original._retry && !isAuthEndpoint && tokens.refresh) {
      original._retry = true;
      try {
        const newAccess = await refreshAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (e) {
        tokens.clear();
        // V2-T9 — toast discret avant redirection
        _maybeToast401Expired(error);
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(e);
      }
    }

    // ── 403 : V2-T9 — message clair + bouton Retour ──────────────────────
    _maybeToast403(error);

    // ── 5xx : toast throttlé ──────────────────────────────────────────────
    _maybeToastServerError(error);

    return Promise.reject(error);
  }
);

// Nettoie un message de validation Pydantic (422) : sans traduction, le toast
// affichait l'anglais brut, ex. "String should match pattern '^\d{4}-\d{2}…'"
// à un chef de chantier au lieu d'un message compréhensible.
function _humanizeValidationMsg(msg) {
  // Les validateurs métier (messages déjà en français) arrivent préfixés
  // de "Value error, " par Pydantic : on retire ce préfixe technique.
  const m = String(msg).replace(/^(?:Value|Assertion) error, /, "");
  if (/^String should match pattern/i.test(m)) return "Format invalide";
  if (/^Field required$/i.test(m)) return "Champ obligatoire";
  const tooShort = m.match(/^String should have at least (\d+) character/i);
  if (tooShort) return tooShort[1] === "1" ? "Champ obligatoire" : `Minimum ${tooShort[1]} caractères`;
  const tooLong = m.match(/^String should have at most (\d+) character/i);
  if (tooLong) return `Maximum ${tooLong[1]} caractères`;
  if (/^Input should be/i.test(m)) return "Valeur invalide";
  return m;
}

/**
 * BUG-025 — clé d'idempotence d'une création sensible.
 *
 * Le serveur dédoublonne les créations portant la même `Idempotency-Key`. Pour
 * que ça serve à quelque chose, la clé doit être fabriquée UNE FOIS par saisie
 * et réutilisée à chaque tentative : c'est ce qui rend inoffensifs le
 * double-clic et le renvoi après coupure réseau. La régénérer à chaque appel
 * reviendrait à ne rien protéger du tout.
 */
export function nouvelleCleIdempotence() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function enTeteIdempotence(cle) {
  return cle ? { headers: { "Idempotency-Key": cle } } : undefined;
}

export function extractErrorMessage(error, fallback = "Une erreur est survenue") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.message) {
    return detail.message;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => _humanizeValidationMsg(d.msg || "")).filter(Boolean).join(" — ") || fallback;
  }
  return fallback;
}

/**
 * Télécharge un fichier servi par une route authentifiée et renvoie une URL
 * blob locale.
 *
 * Une balise `<img src>` ou `<a href>` native n'envoie PAS l'en-tête
 * Authorization — seul l'intercepteur axios ci-dessus l'ajoute. Les signatures,
 * les photos et le PDF d'un rapport d'intervention recevaient donc un 401 :
 * l'écran affichait une image cassée et le bouton « Voir le PDF » ouvrait un
 * onglet contenant du JSON d'erreur.
 *
 * L'appelant est responsable de `URL.revokeObjectURL` (cf. useProtectedMediaUrl).
 */
export async function fetchProtectedBlobUrl(fileUrl) {
  if (!fileUrl) return null;
  // Stockage R2 : l'URL est déjà signée et absolue, elle se charge sans jeton.
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  // Les URL stockées en base sont préfixées "/api/…" alors que l'instance
  // axios porte déjà baseURL "…/api" : sans ce retrait on appellerait /api/api.
  const path = fileUrl.startsWith("/api/") ? fileUrl.slice(4) : fileUrl;
  const res = await api.get(path, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

/**
 * Version hook de `fetchProtectedBlobUrl` : rend l'URL affichable, ou null tant
 * que le fichier n'est pas chargé (ou si son chargement échoue). L'URL blob est
 * révoquée au démontage / changement de fichier pour ne pas fuiter de mémoire.
 */
export function useProtectedMediaUrl(fileUrl) {
  const [href, setHref] = useState(null);

  useEffect(() => {
    if (!fileUrl) {
      setHref(null);
      return undefined;
    }
    let cancelled = false;
    let created = null;
    fetchProtectedBlobUrl(fileUrl)
      .then((url) => {
        if (cancelled) {
          if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }
        if (url && url.startsWith("blob:")) created = url;
        setHref(url);
      })
      .catch(() => {
        if (!cancelled) setHref(null);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [fileUrl]);

  return href;
}

/**
 * Pulls the structured quota payload out of an axios error if present.
 * Returns null when the error is NOT a quota_exceeded 402.
 */
export function extractQuotaExceeded(error) {
  if (error?.response?.status !== 402) return null;
  const d = error?.response?.data?.detail;
  if (d && typeof d === "object" && d.code === "quota_exceeded") return d;
  return null;
}
